import React from "react";
import styled from "styled-components";
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Progress,
  Row,
  Select,
  Skeleton,
  Space,
  Tag,
  Typography,
} from "antd";
import { FilterOutlined, ReloadOutlined } from "@ant-design/icons";
import { DASHBOARD_DATE_PRESETS } from "../dashboardFilters.js";
import RouteProfitabilityWidget from "./RouteProfitabilityWidget.jsx";
import FinanceCommandCentre from "./FinanceCommandCentre.jsx";

const { Text, Title } = Typography;
const { Option } = Select;

const DATE_PRESET_OPTIONS = [
  { value: DASHBOARD_DATE_PRESETS.TODAY, label: "Today" },
  { value: DASHBOARD_DATE_PRESETS.YESTERDAY, label: "Yesterday" },
  { value: DASHBOARD_DATE_PRESETS.THIS_WEEK, label: "This week" },
  { value: DASHBOARD_DATE_PRESETS.LAST_7_DAYS, label: "Last 7 days" },
  { value: DASHBOARD_DATE_PRESETS.THIS_MONTH, label: "This month" },
  { value: DASHBOARD_DATE_PRESETS.LAST_MONTH, label: "Last month" },
  { value: DASHBOARD_DATE_PRESETS.QUARTER, label: "Quarter" },
  { value: DASHBOARD_DATE_PRESETS.YEAR, label: "Year" },
];

const ShellSection = styled.div`
  margin-bottom: 24px;
`;

const ShellCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  height: 100%;

  .ant-card-head-title {
    color: #005249;
    font-weight: 700;
  }
`;

const StatusList = styled.div`
  display: grid;
  gap: 12px;
`;

const StatusRow = styled.div`
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 12px;
`;

const StatusHeader = styled(Space)`
  width: 100%;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const FinanceDashboardShell = ({
  metricGroups,
  dashboardFilters,
  filterControls = null,
  loading = false,
  error = null,
  canView = false,
}) => {
  if (!canView) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Finance dashboard access required"
        description="You need dashboard or finance reporting permissions to view this dashboard."
      />
    );
  }

  if (loading) {
    return (
      <ShellSection data-testid="finance-dashboard-loading">
        <Skeleton active paragraph={{ rows: 6 }} />
      </ShellSection>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Finance dashboard could not load"
        description={error}
      />
    );
  }

  const financeGroup = metricGroups?.finance;
  const metrics = financeGroup?.metrics || [];

  return (
    <ShellSection data-testid="finance-dashboard-shell">
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <ShellCard data-testid="finance-filter-bar">
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
              <Space>
                <FilterOutlined style={{ color: "#005249" }} />
                <Title level={4} style={{ margin: 0 }}>
                  Finance Dashboard
                </Title>
              </Space>
              <FilterSummary dashboardFilters={dashboardFilters} />
            </Space>
            <Text type="secondary">
              Track Bubakii&apos;s financial health from settled jobs — cash,
              contribution, break-even, and conversion pressure.
            </Text>
            <Row gutter={[12, 12]} align="middle">
              <Col xs={24} sm={12} md={8}>
                <Text type="secondary">Date range</Text>
                <Select
                  data-testid="finance-filter-date-preset"
                  style={{ width: "100%", marginTop: 4 }}
                  value={dashboardFilters?.time?.datePreset || DASHBOARD_DATE_PRESETS.LAST_7_DAYS}
                  disabled={!filterControls}
                  onChange={(value) => filterControls?.setTimePreset?.(value)}
                >
                  {DATE_PRESET_OPTIONS.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Button
                  data-testid="finance-filter-reset"
                  icon={<ReloadOutlined />}
                  disabled={!filterControls}
                  onClick={() => filterControls?.resetDashboardFilters?.()}
                  style={{ marginTop: 22 }}
                >
                  Reset filters
                </Button>
              </Col>
            </Row>
          </Space>
        </ShellCard>

        <FinanceCommandCentre commandCentre={financeGroup?.metadata?.commandCentre} />

        <Row gutter={[24, 24]}>
          {[
            ["Revenue Collected", "revenue_collected", "/finance"],
            ["Outstanding Invoices", "outstanding_invoice_value", "/invoices"],
            ["Overdue Invoices", "overdue_invoice_value", "/invoices?status=overdue"],
            ["Refunds", "refund_value", "/finance"],
            ["Held Payouts", "held_payouts", "/payouts"],
            ["Adjustments", "adjustment_value", "/ledger"],
          ].map(([title, metricKey, href]) => (
            <Col xs={24} sm={12} lg={8} key={metricKey}>
              <FinanceMetricCard
                title={title}
                metric={findMetric(metrics, metricKey)}
                href={href}
              />
            </Col>
          ))}
        </Row>

        <Row gutter={[24, 24]}>
          <Col xs={24} lg={14}>
            <PaymentStatusWidget
              paymentStatusDistribution={
                financeGroup?.metadata?.paymentStatusDistribution
              }
            />
          </Col>
          <Col xs={24} lg={10}>
            <DepositExposureCard
              depositExposure={financeGroup?.metadata?.depositExposure}
              sourcePriority={financeGroup?.metadata?.sourcePriority}
            />
          </Col>
        </Row>

        <Row gutter={[24, 24]}>
          <Col xs={24}>
            <RouteProfitabilityWidget
              routeProfitability={financeGroup?.metadata?.routeProfitability}
            />
          </Col>
        </Row>
      </Space>
    </ShellSection>
  );
};

const FilterSummary = ({ dashboardFilters }) => {
  const time = dashboardFilters?.time || {};
  const activeFilters = [
    ["Date", time.datePreset || "last_7_days"],
    [
      "Payment",
      dashboardFilters?.commercial?.paymentStatus ||
        dashboardFilters?.financial?.paymentStatus,
    ],
    ["Route", dashboardFilters?.operational?.route],
  ].filter(([, value]) => value && value !== "all");

  return (
    <Space size={[8, 8]} wrap>
      {activeFilters.map(([label, value]) => (
        <Tag key={label} color="blue">
          {label}: {value}
        </Tag>
      ))}
    </Space>
  );
};

const FinanceMetricCard = ({ title, metric, href }) => (
  <ShellCard title={title}>
    {metric ? (
      <Space direction="vertical" size={8}>
        <a href={href}>
          <Title level={4} style={{ margin: 0 }}>
            {formatCurrency(metric.value)}
          </Title>
        </a>
        <Tag color={metric.changeAbsolute > 0 ? "orange" : "default"}>
          {formatCurrency(metric.changeAbsolute)} vs previous
        </Tag>
      </Space>
    ) : (
      <Empty description="Metric unavailable" image={Empty.PRESENTED_IMAGE_SIMPLE} />
    )}
  </ShellCard>
);

const PaymentStatusWidget = ({ paymentStatusDistribution }) => {
  const statuses = paymentStatusDistribution?.statuses || [];

  if (!statuses.length) {
    return (
      <ShellCard title="Payment Status Distribution">
        <Empty
          description="No payment status data for the selected filters"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </ShellCard>
    );
  }

  return (
    <ShellCard
      title="Payment Status Distribution"
      extra={<Tag color="blue">{statuses.length} statuses</Tag>}
      data-testid="payment-status-widget"
    >
      <StatusList>
        {statuses.map((status) => (
          <StatusRow key={status.drilldownKey} data-drilldown-key={status.drilldownKey}>
            <StatusHeader>
              <Text strong>{status.label}</Text>
              <Text>{formatCurrency(status.value)}</Text>
            </StatusHeader>
            <Progress percent={status.sharePercent} showInfo={false} strokeColor="#005249" />
            <Space size={[6, 6]} wrap>
              <Tag>{status.count} invoices</Tag>
              <Tag>{status.sharePercent}% of value</Tag>
            </Space>
          </StatusRow>
        ))}
      </StatusList>
    </ShellCard>
  );
};

const DepositExposureCard = ({ depositExposure, sourcePriority = [] }) => (
  <ShellCard title="Deposit Exposure">
    <Space direction="vertical" size={12}>
      <Alert
        type="info"
        showIcon
        message="Deposit precision unavailable"
        description={
          depositExposure?.dataAvailability ||
          "Deposit-specific finance fields are not available yet."
        }
      />
      {sourcePriority.map((note) => (
        <Text key={note} type="secondary">
          {note}
        </Text>
      ))}
    </Space>
  </ShellCard>
);

function findMetric(metrics, key) {
  return metrics.find((metric) => metric.key === key);
}

function formatCurrency(value) {
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

export default FinanceDashboardShell;
